import { QueryClient } from "react-query";
import Head from "next/head";
import { dehydrate } from "react-query/hydration";
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { BASE_CATEGORY_SLUG, fetchCategories } from "lib/api";
import {
  useAllCategoriesWithChildren,
  useEvents,
  getUniqueChildCategories,
} from "hooks";
import MainBanner from "components/banners/mainBanner";
import MobileCarousel from "components/carousel/mobileCarousel";
import NewCarousel from "components/carousel/newCarousel";
import Spinner from "components/spinner";

export default function Home() {
  const [worshipEvents, setWorshipEvents] = useState([]);
  const [concertEvents, setConcertEvents] = useState([]);
  const { data: AllChurches } = useAllCategoriesWithChildren();

  const uniqueCategories: any | string[] =
    AllChurches && getUniqueChildCategories(AllChurches);

  const allCategorySlugs: any | string[] = uniqueCategories?.map((c) => c.slug);

  /* Live */
  const { data: liveWorshipEvents } = useEvents(["jumalanpalvelukset"], {
    states: [0],
  });
  /* Public OD & Hidden OD */
  const { data: odWorshipEvents } = useEvents(["jumalanpalvelukset"], {
    states: [2, 1],
    sort: ["state,-publishingDate"],
  });

  /* Live */
  const { data: liveConcertEvents } = useEvents(["konsertit"], {
    states: [0],
  });
  /* Public OD & Hidden OD */
  const { data: odConcertEvent } = useEvents(["konsertit"], {
    states: [2, 1],
    sort: ["state,-publishingDate"],
  });

  const { data: featuredEvents, status: featuredEventsStatus } = useEvents([
    "suosittelemme-etusivu",
  ]);
  const { data: liveEvents } = useEvents(allCategorySlugs, {
    states: [0],
  });
  const { data: upcomingEvents } = useEvents(allCategorySlugs, {
    states: [2, 3],
    startDate: format(new Date(), "yyyy-MM-dd"),
    sort: ["publishingDate"],
  });
  const { data: pastEvents } = useEvents(allCategorySlugs, {
    states: [1, 2],
    endDate: format(new Date(), "yyyy-MM-dd"),
  });

  useEffect(() => {
    const worshipEventsArray = [].concat(
      liveWorshipEvents?.events || [],
      odWorshipEvents?.events || []
    );
    setWorshipEvents(worshipEventsArray);
  }, [liveWorshipEvents, odWorshipEvents]);

  useEffect(() => {
    const concertEventsArray = [].concat(
      liveConcertEvents?.events || [],
      odConcertEvent?.events || []
    );
    setConcertEvents(concertEventsArray);
  }, [liveConcertEvents, odConcertEvent]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <Head>
        <meta property="og:image" key="og:image" content="/images/bg1.jpeg" />
      </Head>
      {featuredEventsStatus === "loading" && <Spinner />}
      {featuredEvents?.events?.length > 0 && (
        <MainBanner dataMainBanner={featuredEvents.events} />
      )}
      <span className="hidden tablet:contents">
        {featuredEvents?.events?.length > 0 && (
          <>
            <NewCarousel
              isChurch={false}
              header={"SUOSITTELEMME"}
              data={featuredEvents.events}
              isLive={false}
              link={false}
            />
            <MobileCarousel
              isChurch={false}
              header={"SUOSITTELEMME"}
              data={featuredEvents.events}
              isLive={false}
              link={false}
            />
          </>
        )}
      </span>
      {featuredEventsStatus === "success" && liveEvents?.events?.length > 0 && (
        <>
          <NewCarousel
            isChurch={false}
            header={"SUORAT LÄHETYKSET"}
            data={liveEvents.events}
            isLive
            link={false}
          />
          <MobileCarousel
            isChurch={false}
            header={"SUORAT LÄHETYKSET"}
            data={liveEvents.events}
            isLive
            link={false}
          />
        </>
      )}
      {featuredEventsStatus === "success" &&
        upcomingEvents?.events?.length > 0 && (
          <>
            <NewCarousel
              isChurch={false}
              header={"TULEVAT LÄHETYKSET"}
              data={upcomingEvents?.events}
              isLive={false}
              upcomingEvent
              link={"tulevat-lahetykset"}
            />
            <MobileCarousel
              isChurch={false}
              header={"TULEVAT LÄHETYKSET"}
              data={upcomingEvents?.events}
              isLive={false}
              upcomingEvent
              link={"tulevat-lahetykset"}
            />
          </>
        )}
      {featuredEventsStatus === "success" && pastEvents?.events?.length > 0 && (
        <>
          <NewCarousel
            isChurch={false}
            header={"MENNEET LÄHETYKSET"}
            data={pastEvents.events}
            isLive={false}
            link={"menneet-lahetykset"}
          />
          <MobileCarousel
            isChurch={false}
            header={"MENNEET LÄHETYKSET"}
            data={pastEvents.events}
            isLive={false}
            link={"menneet-lahetykset"}
          />
        </>
      )}
      {featuredEventsStatus === "success" && AllChurches && (
        <>
          <NewCarousel
            isChurch
            header={"LÄHETYKSET KIRKOITTAIN"}
            data={AllChurches}
            link={"kirkot"}
          />
          <MobileCarousel
            isChurch
            header={"LÄHETYKSET KIRKOITTAIN"}
            data={AllChurches}
            link={"kirkot"}
          />
        </>
      )}
      {featuredEventsStatus === "success" &&
        concertEvents &&
        concertEvents.length > 0 && (
          <>
            <NewCarousel
              header={"KONSERTIT"}
              data={concertEvents}
              isLive={false}
              link={"kategoria/konsertit"}
            />
            <MobileCarousel
              header={"KONSERTIT"}
              data={concertEvents}
              isLive={false}
              link={"kategoria/konsertit"}
            />
          </>
        )}
      {featuredEventsStatus === "success" &&
        worshipEvents &&
        worshipEvents?.length > 0 && (
          <>
            <NewCarousel
              header={"JUMALANPALVELUKSET"}
              data={worshipEvents}
              isLive={false}
              link={"kategoria/jumalanpalvelukset"}
            />
            <MobileCarousel
              header={"JUMALANPALVELUKSET"}
              data={worshipEvents}
              isLive={false}
              link={"kategoria/jumalanpalvelukset"}
            />
          </>
        )}
    </div>
  );
}

export async function getServerSideProps() {
  const queryClient = new QueryClient();

  const categoryQ = {
    slugs: BASE_CATEGORY_SLUG,
    sparams: { include: "children" },
  };

  await queryClient.prefetchQuery(
    ["categories", categoryQ],
    () =>
      fetchCategories(BASE_CATEGORY_SLUG, {
        include: "children",
      }),
    {
      retry: false,
    }
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}
